import { useEffect, useMemo } from 'react';
import { usePublicacoesPublicadasQuery } from '../usePublicacoes';
import { categoriaDocumento } from '../publicacao.definitions';
import type { FormPublicacao } from './publicacaoEditor';

interface DocumentoRelacionadoCampoProps {
  form: FormPublicacao;
  onChange: (parcial: Partial<FormPublicacao>) => void;
}

/**
 * "Documento relacionado": atalho pra um documento oficial da aba Documentos (só os publicados do
 * canal SI). Diferente dos anexos, que são arquivos só deste post. Se o documento pede ciência, o
 * post não tem ciência própria — marca `cienciaPeloDocumento` pro bloco de Ciência avisar.
 */
export function DocumentoRelacionadoCampo({ form, onChange }: DocumentoRelacionadoCampoProps) {
  const { data: publicadas = [] } = usePublicacoesPublicadasQuery();
  const documentos = useMemo(
    () => publicadas.filter((p) => p.tipo === 'documento').sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR')),
    [publicadas]
  );
  const escolhido = documentos.find((d) => d.id === form.documentoRelacionadoId);
  const peloDocumento = Boolean(escolhido?.ciencia?.exige);

  // Ao abrir pra editar (ou o documento mudar de regra), mantém o aviso de ciência em dia.
  useEffect(() => {
    if (form.cienciaPeloDocumento !== peloDocumento) onChange({ cienciaPeloDocumento: peloDocumento });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peloDocumento]);

  return (
    <div className="space-y-1">
      <label htmlFor="documento-relacionado" className="block text-sm font-medium text-raro-gray-700">
        Documento relacionado <span className="font-normal text-raro-gray-500">(da aba Documentos, opcional)</span>
      </label>
      <select
        id="documento-relacionado"
        className="input-primary w-full"
        value={form.documentoRelacionadoId}
        onChange={(event) => onChange({ documentoRelacionadoId: event.target.value })}
      >
        <option value="">Nenhum</option>
        {documentos.map((d) => (
          <option key={d.id} value={d.id}>
            {d.titulo} · v{d.versao ?? 1} ({categoriaDocumento(d.categoria).label})
          </option>
        ))}
      </select>
      <p className="text-xs text-raro-gray-500">
        Um atalho para o documento oficial (com versão e ciência). Os anexos acima são arquivos só deste post.
        {documentos.length === 0 && ' Ainda não há documentos publicados.'}
      </p>
      {peloDocumento && (
        <p className="rounded-lg bg-primary-50 px-3 py-2 text-xs text-primary-800">
          Este documento pede ciência — ela fica só no documento, e o post mostra o botão &quot;Ver documento e
          confirmar&quot;.
        </p>
      )}
    </div>
  );
}
